import React, { FC, useEffect, useState } from 'react';
import { LandscapeProjectsAddProjectsTemplate } from '../../templates';
import { useNavigate, useParams } from 'react-router-dom';
import { ProjectResponseDto } from '../../../store/landscapeAPI/project/dto/response/project-response.dto';
import { ProjectSaveRequestDto } from '../../../store/landscapeAPI/project/dto/request/project-save-request.dto';
import { getUUID } from '../../../utils/UUID';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';
import { MediaSaveResponseDto } from '../../../store/mediaAPI/media/dto/response/media-save-response';
import { saveMedia } from '../../../store/mediaAPI/media/reducers/media.slice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import {
  fetchProjectById,
  saveProject,
} from '../../../store/landscapeAPI/project/reducers/project.slice';
import Spinner from '../../atoms/Spinner/Spinner';
import { LocalesLanguagesConstants } from '../../../locales/locales';
import { PromiseSettledResult } from '../../../utils/types/types';
import { showSuccess } from '../../../utils/Notifications';

interface LandscapeProjectsEditAddProjectsPageProps {}

const LandscapeProjectsEditAddProjectsPage: FC<
  LandscapeProjectsEditAddProjectsPageProps
> = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { id: landscapeId, projectId } = useParams();

  const navigate = useNavigate();

  const [openForSupport, setOpenForSupport] = useState<boolean>(false);

  const [project, setProject] = useState<ProjectResponseDto>({
    backgroundImage: '',
    compactId: '',
    createdByUserId: '',
    createdOnDateTimeUtc: '',
    description: '',
    id: getUUID(),
    language: '',
    name: '',
    openToSupport: false,
    projectEndDateUtc: '',
    projectStartDateUtc: '',
    published: true,
    stories: [],
    target: {
      currentUnit: 0,
      targetUnit: 0,
      qtyUnit: '',
      time: 0,
      timeUnit: '',
      versionId: 0,
      id: getUUID(),
      language: '',
      published: true,
      createdByUserId: '',
      createdOnDateTimeUtc: '',
    },
    versionId: 0,
  });

  const { enrichedProject: existingProject, status } = useSelector(
    (state: RootState) => state.projectSlice
  );

  const { status: translateStatus } = useSelector(
    (state: RootState) => state.translateSlice
  );

  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const translate = async (toLanguage: LocalesLanguagesConstants) => {};

  const generateAllTranslations = async () => {
    if (!projectId || projectId === 'new-project') {
      const rejectedResults: PromiseSettledResult<void>[] = [
        {
          status: 'rejected',
          reason: new Error(
            'Must save verification status before generating translation'
          ),
        },
      ];
      return Promise.reject(rejectedResults);
    }

    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translate)
    );
  };

  const generateTranslation = async () => {
    if (projectId && projectId !== 'new-project') {
      return await translate(toLanguage);
    }
  };

  useEffect(() => {
    if (!projectId || projectId === 'new-project') {
      return;
    }
    dispatch(fetchProjectById({ id: projectId, language: selectedLanguage }));
  }, [dispatch, projectId, selectedLanguage]);

  useEffect(() => {
    if (!existingProject) {
      return;
    }
    setProject(existingProject);
  }, [existingProject]);

  const [imageMeta, setImageMeta] = useState<ImageMeta>();

  const handleImageOnChange = (imageMeta: ImageMeta) => {
    setImageMeta(imageMeta);
  };

  const processMedia = async (
    file: string,
    meta: ImageMeta | undefined
  ): Promise<MediaSaveResponseDto> => {
    if (!meta) {
      return { href: file };
    }
    const formData = new FormData();
    formData.append(
      'file',
      new Blob([file], {
        type: meta.imageType ?? 'image/png',
      }),
      meta.imageSrc
    );
    const response = (await dispatch(
      saveMedia(formData)
    ).unwrap()) as MediaSaveResponseDto;
    return response;
  };

  const handleSubmit = async () => {
    const image = await processMedia(project.backgroundImage ?? '', imageMeta);

    const request: ProjectSaveRequestDto = {
      backgroundImage: image.href,
      compactId: landscapeId!,
      currentUnit: project.target.currentUnit,
      description: project.description,
      goalId: null,
      id: project.id,
      name: project.name,
      openToSupport: openForSupport,
      projectEndDateUtc: project.projectEndDateUtc,
      projectStartDateUtc: project.projectStartDateUtc,
      published: project.published,
      qtyUnit: project.target.qtyUnit,
      targetUnit: project.target.targetUnit,
      time: project.target.time,
      timeUnit: project.target.timeUnit,
    };

    await dispatch(
      saveProject({ saveRequest: request, language: selectedLanguage })
    );

    showSuccess('Project saved successfully.');
    navigate(`/landscapes/edit/${landscapeId}/projects`);
  };

  if (status === 'loading' || translateStatus === 'loading') {
    return <Spinner />;
  }

  return (
    <LandscapeProjectsAddProjectsTemplate
      project={project}
      handleOnChange={setProject}
      handleSubmit={handleSubmit}
      openForSupportSelected={openForSupport}
      setOpenForSupportSelected={setOpenForSupport}
      handleImageOnChange={handleImageOnChange}
      setLanguage={setLanguage}
      selectedLanguage={selectedLanguage}
      fromLanguage={fromLanguage}
      setFromLanguage={setFromLanguage}
      toLanguage={toLanguage}
      setToLanguage={setToLanguage}
      generateTranslation={generateTranslation}
      generateAllTranslations={generateAllTranslations}
    />
  );
};

export default LandscapeProjectsEditAddProjectsPage;
